import { sevenroomsAudienceSchema } from '@models/schema'
import {
  CreateSevenroomsAudienceData,
  SevenroomsAudience,
} from '@models/venue/integrations/SevenroomsAudience'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createSevenroomsAudience as createSevenroomsAudienceAPI,
  fetchSevenroomsAudiences,
} from '@services/venue/integrations//SevenroomsAudienceService'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

const initialState: SBState<SevenroomsAudience> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'sevenroomsAudience',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getSevenroomsAudiencesSuccess,
  setQuery: setSevenroomsAudienceQuery,
  reset: resetSevenroomsAudienceState,
  resetQueryAndIds: resetSevenroomsAudienceQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: SevenroomsAudience }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectSevenroomsAudiences = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedSevenroomsAudience = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectSevenroomsAudienceById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectSevenroomsAudiencesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedSevenroomsAudience =
  (sevenroomsAudience: SevenroomsAudience | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<SevenroomsAudience>(sevenroomsAudience)
      )
    )

export const getSevenroomsAudiences = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchSevenroomsAudiences(params),
    [sevenroomsAudienceSchema],
    slice.actions
  )

export const createSevenroomsAudience = (
  sevenroomsAudience: CreateSevenroomsAudienceData
) =>
  SBAPI.CreateDispatched(
    createSevenroomsAudienceAPI(sevenroomsAudience),
    sevenroomsAudienceSchema,
    slice.actions
  )
